import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { getBrowserPosition } from "@/lib/geo";
import { cn } from "@/lib/utils";

export type PulseLevel = "quiet" | "steady" | "rising" | "surge";

export type PulseSnapshot = {
  postsHour: number;
  postsDay: number;
  commentsHour: number;
  votesHour: number;
  verificationsHour: number;
  alertsActive: number;
  announcementsDay: number;
  projectsDay: number;
  resolvedDay: number;
  nearbyHour: number;
  nearbyDay: number;
  nearbyUrgent: number;
};

export type PulseState = PulseSnapshot & {
  level: PulseLevel;
  /** 0..1 — drives the heartbeat speed and amplitude. */
  intensity: number;
  lastHour: number;
  lastDay: number;
  nearby: number;
  headline: string;
  detail: string;
  loading: boolean;
  place: string | null;
  located: boolean;
  radiusKm: number;
};

const EMPTY: PulseSnapshot = {
  postsHour: 0, postsDay: 0, commentsHour: 0, votesHour: 0, verificationsHour: 0,
  alertsActive: 0, announcementsDay: 0, projectsDay: 0, resolvedDay: 0,
  nearbyHour: 0, nearbyDay: 0, nearbyUrgent: 0,
};

const LEVEL_STYLE: Record<PulseLevel, { dot: string; ring: string; text: string; duration: string }> = {
  quiet: { dot: "bg-muted-foreground", ring: "bg-muted-foreground/25", text: "text-muted-foreground", duration: "4.6s" },
  steady: { dot: "bg-foreground/60", ring: "bg-foreground/20", text: "text-muted-foreground", duration: "3.4s" },
  rising: { dot: "bg-foreground/80", ring: "bg-foreground/25", text: "text-foreground", duration: "2.8s" },
  surge: { dot: "bg-foreground", ring: "bg-foreground/30", text: "text-foreground", duration: "2.3s" },

};

export function pulseLevelStyle(level: PulseLevel) {
  return LEVEL_STYLE[level];
}

/**
 * Weighted intensity: what happens *near you* counts most, then live alerts,
 * then new issues, then the quieter background chatter of votes and replies.
 */
export function pulseIntensity(s: PulseSnapshot) {
  const weighted =
    s.nearbyUrgent * 6 +
    s.alertsActive * 5 +
    s.nearbyHour * 3 +
    s.postsHour * 1.2 +
    s.verificationsHour * 0.6 +
    s.projectsDay * 0.6 +
    s.announcementsDay * 0.4 +
    s.commentsHour * 0.35 +
    s.votesHour * 0.12;
  // Log curve: the first few signals move the needle, then it saturates.
  return Math.max(0, Math.min(1, Math.log1p(weighted) / Math.log1p(45)));
}

export function pulseLevel(intensity: number): PulseLevel {
  if (intensity >= 0.72) return "surge";
  if (intensity >= 0.42) return "rising";
  if (intensity > 0.06) return "steady";
  return "quiet";
}

/**
 * Heartbeat period in seconds. Deliberately unhurried: a calm area barely
 * moves, a surge reads as a steady human pulse rather than a flashing alarm.
 */
export function pulseDuration(intensity: number) {
  return `${(4.6 - intensity * 2.3).toFixed(2)}s`;
}


function describe(s: PulseSnapshot, level: PulseLevel, place: string | null, located: boolean) {
  const where = place ? ` in ${place}` : located ? " around you" : "";

  if (s.alertsActive > 0) {
    return {
      headline: `${s.alertsActive} active alert${s.alertsActive === 1 ? "" : "s"}`,
      detail: s.nearbyUrgent > 0 ? `${s.nearbyUrgent} urgent nearby` : "Live emergency signal on Xamseen",
    };
  }
  if (s.nearbyHour > 0) {
    return {
      headline: `${s.nearbyHour} new report${s.nearbyHour === 1 ? "" : "s"}${where || " nearby"}`,
      detail: s.nearbyDay > s.nearbyHour ? `${s.nearbyDay} in the last 24 hours` : "Reported in the last hour",
    };
  }
  if (level === "surge" || level === "rising") {
    const parts = [
      s.postsHour ? `${s.postsHour} report${s.postsHour === 1 ? "" : "s"}` : null,
      s.commentsHour ? `${s.commentsHour} repl${s.commentsHour === 1 ? "y" : "ies"}` : null,
      s.verificationsHour ? `${s.verificationsHour} verification${s.verificationsHour === 1 ? "" : "s"}` : null,
    ].filter(Boolean);
    return { headline: `Busier than usual${where}`, detail: parts.join(" · ") || "Signals in the last hour" };
  }
  if (level === "steady") {
    const parts = [
      s.postsHour ? `${s.postsHour} new report${s.postsHour === 1 ? "" : "s"}` : null,
      s.commentsHour ? `${s.commentsHour} repl${s.commentsHour === 1 ? "y" : "ies"}` : null,
      s.projectsDay ? `${s.projectsDay} project${s.projectsDay === 1 ? "" : "s"} today` : null,
      s.resolvedDay ? `${s.resolvedDay} resolved today` : null,
    ].filter(Boolean);
    return { headline: `Your community is awake`, detail: parts.slice(0, 2).join(" · ") || "Activity in the last hour" };
  }
  return {
    headline: "Calm right now",
    detail: s.postsDay > 0 ? `${s.postsDay} signal${s.postsDay === 1 ? "" : "s"} in the last 24 hours` : "Nothing new in the last hour",
  };
}

function toState(s: PulseSnapshot, place: string | null, located: boolean, radiusKm: number, loading = false): PulseState {
  const intensity = pulseIntensity(s);
  const level = pulseLevel(intensity);
  return {
    ...s,
    intensity,
    level,
    lastHour: s.postsHour,
    lastDay: s.postsDay,
    nearby: s.nearbyDay,
    loading,
    place,
    located,
    radiusKm,
    ...describe(s, level, place, located),
  };
}

/**
 * Live platform activity: post/comment/vote/verification volume, active
 * emergency alerts, announcements, projects and resolutions — plus a
 * geo-scoped "nearby" slice when the browser shares a position (falling back
 * to the member's city name). Refreshes on an interval and reacts instantly
 * to realtime inserts.
 */
export function usePulse(place?: string | null, radiusKm = 25): PulseState {
  const [state, setState] = useState<PulseState>(() => ({
    ...toState(EMPTY, place ?? null, false, radiusKm, true),
    headline: place ? `What's happening in ${place}` : "What's happening around you",
    detail: "Live community activity",
  }));

  const coordsRef = useRef<{ lat: number; lng: number } | null>(null);
  const locatedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;

    const read = async () => {
      const { data, error } = await supabase.rpc("pulse_snapshot" as never, {
        _lat: coordsRef.current?.lat ?? null,
        _lng: coordsRef.current?.lng ?? null,
        _radius_km: radiusKm,
        _place: place ?? null,
      } as never);
      if (cancelled || error) return;
      const row: any = Array.isArray(data) ? data[0] : data;
      if (!row) return;
      const snap: PulseSnapshot = {
        postsHour: row.posts_hour ?? 0,
        postsDay: row.posts_day ?? 0,
        commentsHour: row.comments_hour ?? 0,
        votesHour: row.votes_hour ?? 0,
        verificationsHour: row.verifications_hour ?? 0,
        alertsActive: row.alerts_active ?? 0,
        announcementsDay: row.announcements_day ?? 0,
        projectsDay: row.projects_day ?? 0,
        resolvedDay: row.resolved_day ?? 0,
        nearbyHour: row.nearby_hour ?? 0,
        nearbyDay: row.nearby_day ?? 0,
        nearbyUrgent: row.nearby_urgent ?? 0,
      };
      setState(toState(snap, place ?? null, locatedRef.current, radiusKm));
    };

    void (async () => {
      if (!coordsRef.current) {
        const pos = await getBrowserPosition();
        if (cancelled) return;
        if (pos) { coordsRef.current = pos; locatedRef.current = true; }
      }
      await read();
    })();

    const t = setInterval(read, 45_000);

    // Unique channel per hook instance — several Pulse surfaces can be mounted at once.
    const ch = supabase
      .channel(`xam-pulse-${Math.random().toString(36).slice(2)}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "posts" }, (p: any) => {
        const row = p?.new ?? {};
        const near =
          coordsRef.current && row.latitude != null && row.longitude != null
            ? Math.abs(row.latitude - coordsRef.current.lat) < radiusKm / 111 &&
              Math.abs(row.longitude - coordsRef.current.lng) < radiusKm / 111
            : place && typeof row.location_name === "string"
              ? row.location_name.toLowerCase().includes(place.toLowerCase())
              : false;
        setState((s) =>
          toState(
            {
              ...(s as PulseSnapshot),
              postsHour: s.postsHour + 1,
              postsDay: s.postsDay + 1,
              nearbyHour: s.nearbyHour + (near ? 1 : 0),
              nearbyDay: s.nearbyDay + (near ? 1 : 0),
              nearbyUrgent: s.nearbyUrgent + (near && (row.severity === "urgent" || row.severity === "critical") ? 1 : 0),
            },
            s.place,
            s.located,
            s.radiusKm,
          ),
        );
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "comments" }, () =>
        setState((s) => toState({ ...(s as PulseSnapshot), commentsHour: s.commentsHour + 1 }, s.place, s.located, s.radiusKm)),
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "votes" }, () =>
        setState((s) => toState({ ...(s as PulseSnapshot), votesHour: s.votesHour + 1 }, s.place, s.located, s.radiusKm)),
      )
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "reactions" }, (p: any) => {
        if (p?.new?.reaction === "verify") {
          setState((s) => toState({ ...(s as PulseSnapshot), verificationsHour: s.verificationsHour + 1 }, s.place, s.located, s.radiusKm));
        }
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "emergency_alerts" }, () => void read())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "community_announcements" }, () => void read())
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "report_status_events" }, () => void read())
      .subscribe();

    return () => { cancelled = true; clearInterval(t); void supabase.removeChannel(ch); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [place, radiusKm]);

  return state;
}

/**
 * The signature Xamseen heartbeat. Deliberately quiet: one soft ring when the
 * area is calm, a second echo ring only once real activity picks up. Tempo,
 * reach, opacity and core travel all scale with measured activity, so it reads
 * as ambient context beside the content — never as a moving distraction.
 */
export function PulseIndicator({
  level = "steady",
  intensity,
  size = 14,
  className,
}: { level?: PulseLevel; intensity?: number; size?: number; className?: string }) {
  const s = LEVEL_STYLE[level];
  const i = typeof intensity === "number" ? Math.max(0, Math.min(1, intensity)) : 0.35;
  const duration = typeof intensity === "number" ? pulseDuration(i) : s.duration;
  const reach = 1.5 + i * 0.55;
  const opacity = 0.2 + i * 0.24;
  const coreScale = 1.02 + i * 0.07;
  // The echo ring is reserved for genuinely busy areas.
  const echo = i >= 0.42 || level === "rising" || level === "surge";
  const style = useMemo(
    () => ({
      width: size * 2,
      height: size * 2,
      ["--pulse-duration" as string]: duration,
      ["--pulse-reach" as string]: String(reach),
      ["--pulse-opacity" as string]: opacity.toFixed(3),
      ["--pulse-core-scale" as string]: coreScale.toFixed(3),
    }),
    [size, duration, reach, opacity, coreScale],
  );

  return (
    <span className={cn("relative inline-grid shrink-0 place-items-center", className)} style={style} aria-hidden>
      <span className={cn("pulse-ring absolute rounded-full", s.ring)} style={{ width: size, height: size }} />
      {echo && (
        <span
          className={cn("pulse-ring absolute rounded-full", s.ring)}
          style={{ width: size, height: size, animationDelay: `calc(${duration} / 2)` }}
        />
      )}
      <span className={cn("pulse-core relative rounded-full", s.dot)} style={{ width: size * 0.5, height: size * 0.5 }} />
    </span>
  );
}


/** Compact strip used in rails and headers. */
export function PulseBanner({ place, className }: { place?: string | null; className?: string }) {
  const pulse = usePulse(place);
  const s = LEVEL_STYLE[pulse.level];
  return (
    <div className={cn("panel panel-accent flex items-center gap-3 px-4 py-3.5", className)}>
      <PulseIndicator level={pulse.level} intensity={pulse.intensity} size={16} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold">{pulse.headline}</p>
        <p className={cn("truncate text-xs", s.text)}>{pulse.detail}</p>
      </div>
    </div>
  );
}
